import { Button, Pending } from '@cluey/cluey-components';
import moment from 'moment';
import { useMemo } from 'react';

import { api } from '../../../../api';
import type { Enrolment } from '../../../../api/types/enrolment';
import CheckIcon from '../../../../assets/images/icon-outline-check-tick.svg';
import RewardIcon from '../../../../assets/images/icon-outline-reward-gift.svg';
import CardLayout from '../../../../common/hub/CardLayout';
import { UserPricingPlan } from '../../../../types/hubTypes';
import pluralize from '../../../../util/pluralize';
import { SkeletonLoader } from '../../../SkeletonLoader';

interface CancelInterruptProps {
  enrolment: Enrolment;
  pricingPlan: UserPricingPlan;
  goBack(): void;
  continueCancel(): void;
}

const CancelInterrupt = ({ enrolment, pricingPlan, goBack, continueCancel }: CancelInterruptProps) => {
  const { data: completedSessions, isLoading: isCompletedSessionsLoading } = api.session.all.useQuery('completed');
  const { data: cancelledSessions, isLoading: isCancelledSessionsLoading } = api.session.all.useQuery('cancelled');

  const { loyaltyVoucherNumber, loyaltyVouchersSessionCompletion } = pricingPlan;
  const { numOfLoyaltyQualifiedSessions } = enrolment;
  const loyaltySessionsRequired = loyaltyVouchersSessionCompletion ?? 0;
  const loyaltySessionsCompleted = numOfLoyaltyQualifiedSessions ?? 0;
  const remainingSessionsToComplete = loyaltySessionsRequired - loyaltySessionsCompleted || loyaltySessionsRequired;

  const enrolmentLoyaltySessions = useMemo(() => {
    if (!isCompletedSessionsLoading && !isCancelledSessionsLoading && completedSessions && cancelledSessions) {
      const enrolmentSessions = [...completedSessions, ...cancelledSessions];
      return enrolmentSessions
        .filter((session) => session.uniqueEnrolmentId === enrolment.enrolmentId && session.loyaltyQualified)
        .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
    }
    return [];
  }, [
    completedSessions,
    enrolment.enrolmentId,
    cancelledSessions,
    isCompletedSessionsLoading,
    isCancelledSessionsLoading,
  ]);

  return (
    <CardLayout className="poppins">
      <div className="flex flex-col items-center justify-center gap-y-6" id="cancel-interrupt-card">
        <img src={RewardIcon} alt="Reward" width={60} height={60} />
        <h2 className="merriweather text-[32px] font-bold">Wait! Don't miss your reward</h2>
        <p className="text-lg">
          You are only{' '}
          <span className="font-semibold">
            {remainingSessionsToComplete} {pluralize('session', remainingSessionsToComplete)} away
          </span>{' '}
          from receiving{' '}
          {loyaltyVoucherNumber <= 1 ? (
            <>
              a <span className="font-semibold">free session voucher</span>
            </>
          ) : (
            <span className="font-semibold">{loyaltyVoucherNumber} free session vouchers</span>
          )}
        </p>
        <Pending
          loading={isCompletedSessionsLoading || isCancelledSessionsLoading}
          loader={
            <>
              <SkeletonLoader
                loading={isCompletedSessionsLoading || isCancelledSessionsLoading}
                className="h-5 w-1/2"
              />
              <SkeletonLoader
                loading={isCompletedSessionsLoading || isCancelledSessionsLoading}
                className="h-5 w-1/2"
              />
            </>
          }
        >
          {enrolmentLoyaltySessions.length > 0 && (
            <div className="flex w-full flex-col items-center gap-y-2">
              {enrolmentLoyaltySessions.map((session) => (
                <div className="flex flex-row items-center gap-x-2" key={session.studentSessionId}>
                  <img src={CheckIcon} alt="Check" width={18} height={18} />
                  <p className="poppins text-base text-[#686868]">
                    {moment(session.startTime).format('ddd D MMMM [at] hh:mma')}–
                    {moment(session.endTime).format('hh:mma')}
                  </p>
                </div>
              ))}
            </div>
          )}
        </Pending>
        <p className="text-lg font-semibold">Cancelling now means you will give up on your reward</p>
        <div className="flex w-auto flex-col items-center gap-y-3">
          <Button className="ctaButton !px-20" size="sm" onClick={goBack}>
            Continue learning
          </Button>
          <Button
            className="ctaButton w-fit border-none !bg-transparent !px-1 !py-0 !font-normal !text-grey-6"
            size="sm"
            appearance="reverse"
            onClick={continueCancel}
          >
            I still want to cancel
          </Button>
        </div>
      </div>
    </CardLayout>
  );
};

export default CancelInterrupt;
